<template>
  <div id="digital-event-room-presentation" class="content-panel">
    <div class="room-header clearfix">
      <div class="room-title">
        <span v-if="room.SHOW_TITLE">{{ room.TITLE }}</span>
      </div>

      <div class="room-controls">
        <div class="room-guide">
          <modal-window-link
            modal-window-name="iframe-modal-window"
            :context="{ title: 'Awards Room Guide', url: 'https://awardsroom.com/support/en/page/in-room-guide' }"
          >
            <template slot-scope="{ open }">
              <a href="javascript:void(0);" class="btn btn-default" @click="open">
                <i class="ar-icon icon-question-square-light" aria-hidden="true"></i> Help
              </a>
            </template>
          </modal-window-link>
        </div>

        <div class="room-logout">
          <a href="javascript:void(0);" class="btn btn-default" @click="logout"
            ><i class="ar-icon icon-sign-out-alt-light" aria-hidden="true"></i> Logout</a
          >
        </div>
      </div>
    </div>
    <div class="content-body">
      <digital-event-room-list-select
        v-if="rooms.length > 1 && showRooms"
        :rooms="rooms"
      ></digital-event-room-list-select>
      <div
        v-if="room.MULTIPLE_VIDEO_CHANNELS === 'true' && room.children.DigitalEventVideoChannel"
        class="channel-switcher"
      >
        <digital-event-channel-select
          :options="room.children.DigitalEventVideoChannel"
          name-key="TITLE"
          value-key="TITLE"
          class="select"
          select-label="Select channel"
          @channel-selection="channelSelect($event)"
        ></digital-event-channel-select>
      </div>
      <div class="stage">
        <div class="video-wrap" ref="videoWrap">
          <div
            class="video"
            :class="room.MULTIPLE_VIDEO_CHANNELS === 'true' ? channel.STREAM_TYPE : room.STREAM_TYPE"
            ref="video"
          >
            <digital-event-room-video-container :player="player"></digital-event-room-video-container>
            <template v-if="videoChatEnabled && currentUserPermissions.canVideoChat">
              <transition :duration="{ enter: 1000, leave: 200 }" name="fade">
                <div v-show="showMyVideoPanel" :class="showMyVideoPanel ? 'show' : 'no-show'">
                  <digital-event-room-video-chat v-if="deviceError === false" :options="{}">
                  </digital-event-room-video-chat>

                  <div class="video-error" v-else>
                    <div class="error-icon">
                      <modal-window-link
                        modal-window-name="iframe-modal-window"
                        :context="{
                          title: 'Awards Room Guide',
                          url: 'https://awardsroom.com/support/en/page/in-room-video-guide'
                        }"
                      >
                        <template slot-scope="{ open }">
                          <a href="javascript:void(0);" @click="open">
                            <div class="icons">
                              <i class="ar-icon icon-video-slash-solid" aria-hidden="true"></i>
                            </div>
                            <div class="icon-msg">Video chat disabled</div>
                          </a>
                        </template>
                      </modal-window-link>
                    </div>
                    <div class="error-message" v-if="deviceError === null">
                      <ul>
                        <li>In order to use video chat functionality, please enable access to your media devices.</li>
                        <li>This must be configured in your browser - click the icon to view the guide.</li>
                      </ul>
                    </div>
                    <div class="error-message" v-if="deviceError === 'OT_USER_MEDIA_ACCESS_DENIED'">
                      <ul>
                        <li>You have blocked access to your device's camera/microphone.</li>
                        <li>In order to use video chat functionality, please enable access to your media devices.</li>
                        <li>This must be configured in your browser - click the icon to view the guide.</li>
                      </ul>
                    </div>
                    <div class="error-message" v-else-if="deviceError === 'OT_HARDWARE_UNAVAILABLE'">
                      <ul>
                        <li>We are unable to access your camera/microphone.</li>
                        <li>It seems likely that these devices are active in a different application.</li>
                        <li>Please turn off the camera in all other places in order to be able to use it here.</li>
                      </ul>
                    </div>
                    <div
                      class="error-message"
                      v-if="deviceError === 'OT_NOT_SUPPORTED' || deviceError === 'OT_NO_DEVICES_FOUND'"
                    >
                      <ul>
                        <li>We are unable to access your camera/microphone.</li>
                        <li>
                          It is possible that you have an insecure connection - please check the the site is loaded with
                          https:// in the address bar.
                        </li>
                        <li>If the issue persists please contact event support.</li>
                        <li>
                          <modal-window-link
                            modal-window-name="iframe-modal-window"
                            :context="{
                              title: 'Awards Room Guide',
                              url: 'https://awardsroom.com/support/en/page/in-room-video-guide'
                            }"
                          >
                            <template slot-scope="{ open }">
                              <a href="javascript:void(0);" @click="open">
                                <i class="fa fa-question-circle" aria-hidden="true"></i> Video / audio help
                              </a>
                            </template>
                          </modal-window-link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </transition>
              <div class="toggle-panel" :class="showMyVideoPanel ? 'is-open' : 'is-closed'">
                <div class="video-pin">
                  <a href="javascript:void(0);" @click="toggleFloat">
                    <i
                      aria-hidden="true"
                      class="ar-icon"
                      :class="pinOnScroll ? 'icon-paperclip-light' : 'icon-arrows-v-light'"
                    ></i>
                    {{ pinOnScroll ? "Dock" : "Float" }}
                  </a>
                </div>
                <template v-if="locks.toggleVideoPanel">
                  <span>
                    ...
                  </span>
                </template>
                <template v-else>
                  <template v-if="videoChatIsActive">
                    <div class="toggle-bar inactive">
                      <span class="action-txt">
                        Cannot close during a video call
                      </span>
                      <i class="fa fa-chevron-up fa-lg" aria-hidden="true"></i>
                    </div>
                  </template>
                  <template v-if="!videoChatIsActive">
                    <a href="javascript:void(0);" class="toggle-bar toggle-link" @click="toggleMyVideoPanel">
                      <template v-if="showMyVideoPanel">
                        <span class="action-txt">
                          {{ room.VIDEO_CHATS_ENABLED && deviceError ? "Close panel" : "Close video chat" }}
                        </span>
                        <i class="fa fa-chevron-up fa-lg" aria-hidden="true"></i>
                      </template>
                      <template v-if="!showMyVideoPanel">
                        <i class="ar-icon icon-video-slash-solid" aria-hidden="true"></i>
                        <span class="action-txt">
                          &nbsp;Video chat closed. Open
                        </span>
                        <i class="fa fa-chevron-down fa-lg" aria-hidden="true"></i>
                      </template>
                    </a>
                  </template>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VideoChat from "@/components/DigitalEvent/RoomList/Room/VideoChat/VideoChat";
import ChannelSelect from "@/components/DigitalEvent/RoomList/Room/CustomSelect";
import BaseMixin from "@/components/DigitalEvent/mixins/BaseMixin";
import RoomListSelect from "./components/RoomListSelect.vue";
import { ROOMS_NAVIGATION_STYLE } from "@/helpers/constants";
import { mapGetters, mapState } from "vuex";
import VideoContainer from "@/components/DigitalEvent/RoomList/Room/Presentation/VideoContainer.vue";

export default {
  name: "Presentation",
  components: {
    "digital-event-room-video-chat": VideoChat,
    "digital-event-channel-select": ChannelSelect,
    "digital-event-room-list-select": RoomListSelect,
    "digital-event-room-video-container": VideoContainer
  },
  props: {
    pinnedOptions: {
      type: Object,
      default: function() {
        return {};
      }
    },
    defaultStyles: {
      type: Object,
      default: function() {
        return {
          height: "auto",
          width: "100%"
        };
      }
    }
  },
  mixins: [BaseMixin],
  data() {
    return {
      pinOnScroll: false,
      defaultOptions: {},
      showMyVideoPanel: true,
      channel: {}
    };
  },
  computed: {
    ...mapGetters({
      rooms: "room/rooms",
      multipleRooms: "room/multipleRooms"
    }),
    ...mapState({
      deviceError: state => state.calls.devices.deviceError,
      event: state => state.event.context
    }),
    ...mapGetters({
      videoChatEnabled: "videoChat/videoChatEnabled",
      currentUserPermissions: "users/currentUserPermissions",
      room: "room/activeRoom"
    }),
    videoChatIsActive() {
      return (
        this.$store.state.videoChat.activeVideoSession || this.$store.getters["invites/sentVideoCallInvites"].length
      );
    },
    /**
     * define the object for rendering the player
     * @returns player object
     */
    player() {
      if (this.room.MULTIPLE_VIDEO_CHANNELS === "true") {
        return {
          uuid: this.channel.uuid,
          title: this.channel.TITLE,
          startTime: this.channel.SESSION_START_TIME,
          streamType: this.channel.STREAM_TYPE,
          youtubeCode: this.channel.VIDEO_CODE_YOUTUBE,
          vimeoUrl: this.channel.VIDEO_URL_VIMEO,
          customPlayer: this.channel.VIDEO_FEED,
          presentationImage: this.channel.VIDEO_BG_IMAGE,
          presentationHTML: this.channel.VIDEO_BG_HTML,
          videoJsOptions: {
            autoplay: true,
            controls: true,
            muted: true,
            sources: [
              {
                src: this.channel.VIDEO_URL_RTMP,
                type: "application/x-mpegURL"
              }
            ]
          }
        };
      } else {
        return {
          uuid: this.room.uuid,
          title: this.room.TITLE,
          startTime: this.room.TIME_EVENT_START,
          streamType: this.room.STREAM_TYPE,
          youtubeCode: this.room.VIDEO_CODE_YOUTUBE,
          vimeoUrl: this.room.VIDEO_URL_VIMEO,
          customPlayer: this.room.VIDEO_FEED,
          presentationImage: this.room.VIDEO_BG_IMAGE,
          presentationHTML: this.room.VIDEO_BG_HTML,
          videoJsOptions: {
            autoplay: true,
            controls: true,
            muted: true,
            sources: [
              {
                src: this.room.VIDEO_URL_RTMP,
                type: "application/x-mpegURL"
              }
            ]
          }
        };
      }
    },
    isMultipleChannels() {
      return this.room.MULTIPLE_VIDEO_CHANNELS;
    },
    channelsLength() {
      return this.room.children.DigitalEventVideoChannel ? this.room.children.DigitalEventVideoChannel.length : 0;
    },
    locks() {
      return this.$store.state.locks;
    },
    showRooms() {
      return this.event.ROOMS_NAVIGATION_STYLE === ROOMS_NAVIGATION_STYLE.DROPDOWN;
    },
    channelType() {
      return this.room.STREAM_TYPE;
    }
  },
  methods: {
    /**
     * Unpin video container
     */
    unpin() {
      this.pinOnScroll = false;

      this.$refs.videoWrap.height = "auto";
      this.$refs.video.classList.remove("pinned");

      Object.entries(this.defaultOptions).forEach(data => {
        this.$refs.video.style[data[0]] = data[1];
      });
    },
    toggleFloat() {
      this.pinOnScroll = !this.pinOnScroll;
      if (!this.pinOnScroll) {
        this.$refs.video.classList.remove("pinned");
      }
    },
    channelSwitcher(channel) {
      this.channel = channel;
    },
    /**
     * Trigger a new event for when the player has loaded
     * also called if the channel is switched
     */
    notifyLoaded() {
      // use a pure JS event so it can be caught outside of view context e.g. in CType js field
      let videoPlayerLoaded = new CustomEvent("videoPlayerLoaded", {
        detail: {
          roomUUID: this.room.uuid,
          roomSlug: this.room.SLUG,
          player: this.player
        }
      });
      // Dispatch the event
      window.dispatchEvent(videoPlayerLoaded);
    },
    /**
     * Scroll handler
     */
    handleScroll() {
      if (!this.pinOnScroll) {
        return;
      }

      let videoHeight = this.$refs.video.offsetHeight;
      let windowScrollTop = document.body.scrollTop;
      let rect = this.$refs.videoWrap.getBoundingClientRect();
      let videoBottom = rect.top + document.body.scrollTop;

      if (windowScrollTop > videoBottom) {
        this.$refs.videoWrap.height = videoHeight;
        this.$refs.video.classList.add("pinned");

        Object.entries(this.pinnedOptions).forEach(data => {
          this.$refs.video.style[data[0]] = data[1];
        });
      } else {
        this.$refs.videoWrap.height = "auto";
        this.$refs.video.classList.remove("pinned");

        Object.entries(this.defaultOptions).forEach(data => {
          this.$refs.video.style[data[0]] = data[1];
        });
      }
    },
    /**
     * Open or close the user's video panel
     */
    async toggleMyVideoPanel() {
      this.$store.commit("setLock", { name: "toggleVideoPanel", lock: true });

      this.showMyVideoPanel = !this.showMyVideoPanel;
      if (this.$store.getters["room/activeRoom"].VIDEO_CHATS_ENABLED) {
        await this.$store.dispatch("videoChat/toggleVideoChatsFunctionality", this.showMyVideoPanel);
      }

      this.$store.commit("setLock", { name: "toggleVideoPanel", lock: false });
    },
    channelSelect(channel) {
      this.channel = channel;
      this.notifyLoaded();
    }
  },
  watch: {
    isMultipleChannels: function(val) {
      val
        ? (this.channel = this.room.children.DigitalEventVideoChannel
            ? this.room.children.DigitalEventVideoChannel[0]
            : {})
        : this.$forceUpdate();
    },
    channelsLength: function(val) {
      val ? (this.channel = this.room.children.DigitalEventVideoChannel[0]) : (this.channel = {});
    },
    isNetworkingAllowed: async function(val) {
      await this.$store.dispatch("videoChat/toggleVideoChatsFunctionality", val);
    },
    channelType: function(val) {
      if (val === "custom") {
        this.notifyLoaded();
      }
    }
  },
  async created() {
    await this.$store.dispatch("videoChat/toggleVideoChatsFunctionality");
    window.addEventListener("scroll", this.handleScroll);
    if (this.room.MULTIPLE_VIDEO_CHANNELS === "true") {
      this.channel = this.room.children.DigitalEventVideoChannel ? this.room.children.DigitalEventVideoChannel[0] : {};
    }
  },
  mounted() {
    // Save default styles
    this.defaultOptions.height = this.defaultStyles.height || getComputedStyle(this.$refs.video)["height"];
    this.defaultOptions.width = this.defaultStyles.width || getComputedStyle(this.$refs.video)["width"];
    this.defaultOptions.left = this.defaultStyles.left || getComputedStyle(this.$refs.video)["left"];
    this.defaultOptions.top = this.defaultStyles.top || getComputedStyle(this.$refs.video)["top"];
    this.defaultOptions.right = this.defaultStyles.right || getComputedStyle(this.$refs.video)["right"];
    this.defaultOptions.bottom = this.defaultStyles.bottom || getComputedStyle(this.$refs.video)["bottom"];
    this.notifyLoaded();
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  }
};
</script>
